import {
  Eye,
  Trash2,
  Circle,
  CircleAlert,
  SquarePen,
  Wrench, // Added for maintenance icon
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";
import { format } from "date-fns"; // Recommended for date formatting

const IMAGE_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const cardHoverClass =
  "bg-white dark:bg-card rounded-2xl border border-[#E8DDD3] dark:border-border shadow-sm " +
  "transition-all duration-300 ease-in-out " +
  "hover:border-[#e8d0aa] dark:hover:border-primary/50 hover:shadow-[5px_5px_0px_0px_#e8d0aa] dark:hover:shadow-[5px_5px_0px_0px_rgba(232,208,170,0.2)] hover:-translate-y-1 " +
  "hover:scale-[1.00] cursor-pointer relative overflow-hidden";

const StatusBadge = ({ status }) => {
  const styles = {
    Available:
      "bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-100 dark:border-green-800/50",
    "On Rent":
      "bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400 border border-orange-100 dark:border-orange-800/50",
    "Under Maintenance":
      "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-800/50",
  };

  const icons = {
    Available: <Circle className="w-2.5 h-2.5 flex-shrink-0" />,
    "On Rent": <CircleAlert className="w-2.5 h-2.5 flex-shrink-0" />,
    "Under Maintenance": <Wrench className="w-2.5 h-2.5 flex-shrink-0" />,
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 ${
        styles[status] || ""
      }`}
    >
      {icons[status]}
      <span>{status}</span>
    </span>
  );
};

const OwnEquipmentCard = ({
  item,
  handleOpenEquipmentDetails,
  handleEditEquipment,
  handleDeleteEquipment,
}) => {
  const { theme } = useTheme();

  const equipmentName =
    item.equipment?.equipment_name ||
    item.equipment_name ||
    "Unknown Equipment";

  const hasPhotos =
    item.crew_equipment_photos && item.crew_equipment_photos.length > 0;
  const displayImage = hasPhotos
    ? `${IMAGE_BASE_URL}${item.crew_equipment_photos[0].file_url}`
    : "/images/misc/equipment_1.png";

  const category = item.category || "Equipment";
  const description = item.description || "";
  // const marketPrice = item.market_price || 0; // Commented out
  // const rentalPrice = item.rental_price || 0; // Commented out
  // const rentalPriceType = item.rental_price_type === 1 ? "hour" : "day"; // Commented out

  // Logic for status based on API response
  const status = item.equipment_on_maintenance === 1 ? "Under Maintenance" : "Available";
  // Logic for last maintenance date
  const lastMaintenance = item.last_maintenance_date
  ? format(new Date(item.last_maintenance_date), "MMM dd, yyyy")
  : "Not recorded";

  return (
    <div
      key={item.crew_equipment_id || item.equipment_id}
      className={cn(
        cardHoverClass,
        theme === "dark"
          ? "bg-card"
          : "bg-[linear-gradient(0deg,#FFF_40%,#E7DDD4_100%)] bg-[#D3D3D3]"
      )}
    >
      {/* Image Container */}
      <div className="relative overflow-hidden group h-[196px] bg-transparent">
        {/* Tag */}
        <span className="absolute top-3 left-3 bg-white dark:bg-background text-black dark:text-foreground text-sm font-medium px-3 py-1 rounded-full shadow-sm z-20">
          {category}
        </span>

        <img
          src={displayImage}
          alt={equipmentName}
          onError={(e) => {
            e.target.src = "/images/misc/equipment_1.png";
          }}
          className="mx-auto h-full w-full object-contain transition-transform duration-300 ease-out group-hover:scale-110 relative z-10"
        />
      </div>

      {/* Content */}
      <div className="mt-2 px-6 py-2 bg-transparent">
        <div className="flex items-start justify-between gap-6">
          <h3 className="font-semibold text-base leading-6 truncate w-2/3 text-gray-900 dark:text-foreground">
            {equipmentName}
          </h3>
          <StatusBadge status={status} />
        </div>

        <p className="text-sm text-[#737373] dark:text-muted-foreground md:mt-1 leading-5 truncate min-w-0">
          {description || "No description"}
        </p>

        {/* Updated section to show Last Maintenance Date */}
        <div className="md:mt-3 text-sm font-medium text-[#1F2937] dark:text-foreground">
          <div className="flex items-center gap-2 text-[#737373] dark:text-muted-foreground">
            <Wrench className="w-3.5 h-3.5" />
            <span>Last Maintenance: {lastMaintenance}</span>
          </div>
          {/* Market Price Hidden */}
          {/* <span className="block">Market Price: ${marketPrice || "0"}</span> */}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 xl:px-6 xl:py-4 border-t border-[#F5EBDA] dark:border-border flex flex-wrap items-center justify-end bg-white dark:bg-card">
        {/* Rental Price Hidden */}
        {/* <div className="flex items-center gap-2 font-medium text-xs text-gray-900 dark:text-foreground">
          <CircleDollarSign className="w-4 h-4" />
          <span>
            ${rentalPrice}/{rentalPriceType}
          </span>
        </div> */}

        <div className="flex flex-wrap gap-2 mt-2 xl:mt-0">
          <button
            onClick={() => handleOpenEquipmentDetails(item.crew_equipment_id)}
            className="w-9 h-9 rounded-lg bg-[#F5F5F5] dark:bg-background flex items-center justify-center shadow hover:bg-[#262626] dark:hover:bg-primary group"
          >
            <Eye className="w-4 h-4 text-gray-900 dark:text-foreground group-hover:text-white" />
          </button>

          <button
            onClick={() => handleEditEquipment(item)}
            className="w-9 h-9 rounded-lg bg-[#F5F5F5] dark:bg-background flex items-center justify-center shadow hover:bg-[#262626] dark:hover:bg-primary group"
          >
            <SquarePen className="w-4 h-4 text-gray-900 dark:text-foreground group-hover:text-white" />
          </button>

          <button
            onClick={() => handleDeleteEquipment(item.crew_equipment_id)}
            className="w-9 h-9 rounded-lg bg-[#F5F5F5] dark:bg-background flex items-center justify-center shadow hover:bg-[#FEE2E2] dark:hover:bg-red-900/40"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnEquipmentCard;
